import { Box, Container, Divider, Grid, Typography } from "@material-ui/core";
import React, { useEffect, useState } from "react";

import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  BUILDING_TYPE_KEY,
  MEASUREMENT_UNIT,
  MITIGATION_SYSTEM_KEY,
  REPORT_RESULT_COLOR,
  REPORT_RESULT_SU,
  ROOM_TYPE_KEY,
  STRUCTURE_TYPE_KEY,
  TEST_FLOOR_KEY,
  UNIT_CONVERSION_SCALE,
} from "../../constants/values";
import { useLocation } from "react-router-dom";
import { convertToPicocuries, getRadonValue } from "./util";
import { Auth } from "aws-amplify";
import { getCompanyInfo } from "../../models/services/authService";
import { RadonUtil } from "../components/RadonUtil";
import moment from "moment";

const PageMain = ({ reportDetails, reportChartRadonValue }) => {
  const location = useLocation();

  const [user, setUser] = useState();
  const [companyInfo, setCompanyInfo] = useState();

  const getGeneratedDate = () => {
    let genDate = location.state.originalGenerated;
    return genDate;
  };
  let generated = RadonUtil.getDate(moment(getGeneratedDate()), 3);

  const styles = {
    reportItemTitle: {
      color: "black",
      fontSize: "10px",
      fontWeight: 500,
      textAlign: "left",
      fontStyle: "italic",
    },

    reportItemCompany: {
      color: "black",
      fontSize: "12px",
      fontWeight: 350,
      textAlign: "left",
      whiteSpace: "pre-line",
    },

    reportItemContent: {
      marginTop: 5,
      color: "black",
      fontSize: "11px",
      fontWeight: 500,
      textAlign: "left",
      whiteSpace: "pre-line",
      wordWrap: "break-word",
    },

    reportItemText: {
      color: "black",
      fontSize: "11px",
      fontWeight: 500,
    },

    companyAddressText: {
      color: "black",
      fontSize: "12px",
    },

    contentText: {
      color: "black",
      fontSize: "11px",
      fontWeight: 500,
      marginLeft: 5,
    },

    titleTopText: {
      color: "black",
      fontSize: "11px",
      fontWeight: 300,
      marginTop: 5,
      textAlign: "left",
    },

    titleText: {
      color: "black",
      fontSize: "11px",
      fontWeight: 300,
      textAlign: "left",
    },

    report_footer: {
      fontWeight: 300,
      fontSize: "10px",
      // lineHeight: "0px",
      color: "#585858",
    },
  };

  const reformatRadonData = (data, unit, point) => {
    if (data === "" || data === undefined) {
      return "N/A";
    } else {
      if (unit === 0) {
        return (data / UNIT_CONVERSION_SCALE).toFixed(point) + " pCi/L";
      } else {
        return data.toFixed(0) + " Bq/m3";
      }
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Container>
          <p className="label">{`Data #: ${label}`}</p>
          <p className="label">
            {/* <span style={{ color: theme.palette.primary.main }}>Radon: </span> */}
            <span>{`${payload[0].value} ${
              MEASUREMENT_UNIT[reportDetails.measurement_unit]
            }`}</span>
          </p>
        </Container>
      );
    }

    return null;
  };

  const deviceLocationReturn = () => {
    if (reportDetails.device_location.street === "") {
      return <Typography style={styles.reportItemText}>N/A</Typography>;
    }

    if (reportDetails.device_location === undefined) {
      return <Typography style={styles.reportItemText}>N/A</Typography>;
    }

    return (
      <Typography style={styles.reportItemText}>
        {reportDetails.device_location.street +
          " " +
          reportDetails.device_location.detail +
          "\n" +
          reportDetails.device_location.city +
          " " +
          reportDetails.device_location.state +
          " " +
          reportDetails.device_location.zipcode}
      </Typography>
    );
  };

  const clientReturn = () => {
    const clientName = reportDetails.client_name;
    const clientEmail = reportDetails.client_email;
    const clientPhone = reportDetails.client_phone;

    const noName = clientName === "" || undefined;
    const noEmail = clientEmail === "" || undefined;
    const noPhone = clientPhone === "" || undefined;

    if (noName && noEmail && noPhone) {
      return <Typography style={styles.reportItemText}>N/A</Typography>;
    }

    return (
      <>
        {clientName && (
          <Typography style={styles.reportItemText}>{clientName}</Typography>
        )}
        {clientEmail && (
          <Typography style={styles.reportItemText}>{clientEmail}</Typography>
        )}
        {clientPhone && (
          <Typography style={styles.reportItemText}>{clientPhone}</Typography>
        )}
      </>
    );
  };

  const testByReturn = () => {
    if (user === undefined) {
      return <Typography style={styles.reportItemText}>N/A</Typography>;
    }

    return (
      <>
        {user.name && (
          <Typography style={styles.reportItemText}>{user.name}</Typography>
        )}
        {user.email && (
          <Typography style={styles.reportItemText}>{user.email}</Typography>
        )}
        {user["custom:certNo"] && (
          <Typography style={styles.reportItemText}>
            {user["custom:certNo"]}
          </Typography>
        )}
      </>
    );
  };

  const companyAddressReturn = () => {
    if (companyInfo === undefined) {
      return <Typography style={styles.companyAddressText}>N/A</Typography>;
    } else {
      return (
        <>
          {companyInfo.companyName && (
            <Typography style={styles.companyAddressText}>
              {companyInfo.companyName}
            </Typography>
          )}
          {companyInfo.address.street && (
            <Typography style={styles.companyAddressText}>
              {companyInfo.address.street}
              {companyInfo.address.detail
                ? " " + companyInfo.address.detail
                : ""}
            </Typography>
          )}
          <Typography style={styles.companyAddressText}>
            {companyInfo.address.city +
              " " +
              companyInfo.address.state +
              " " +
              companyInfo.address.zipcode}
          </Typography>
        </>
      );
    }
  };

  useEffect(() => {
    const getUser = async () => {
      const user = await Auth.currentAuthenticatedUser();
      setUser(user.attributes);
    };

    getUser();

    const getCompany = async () => {
      const trimmedEmail = reportDetails.email.trim().toLowerCase();

      const companyInfo = (await getCompanyInfo(trimmedEmail)).data;
      setCompanyInfo(companyInfo);
    };

    getCompany();
  }, [reportDetails]);

  return (
    <div>
      <div>
        <Grid
          style={{
            marginTop: "15px",
            flexDirection: "row",
          }}
          container
        >
          <Grid
            item
            xs={4}
            style={{
              backgroundColor: "white",
            }}
          />

          <Grid
            item
            xs={4}
            style={{
              display: "flex",
              backgroundColor: "white",
              justifyContent: "center",
            }}
          >
            <img
              style={{
                objectFit: "scale-down",
                width: "160px",
                height: "60px",
              }}
              crossorigin="anonymous"
              src={reportDetails.company_logo_url}
              alt=""
            />
          </Grid>

          <Grid
            item
            xs={4}
            style={{
              paddingLeft: "3rem",
              backgroundColor: "white",
              alignItems: "center",
              display: "flex",
            }}
          >
            <Typography style={styles.reportItemCompany}>
              {companyAddressReturn()}
            </Typography>
          </Grid>
        </Grid>

        <Typography
          style={{
            paddingLeft: 20,
            marginBottom: 10,
            textAlign: "start",
            fontWeight: 800,
            color: "black",
            fontSize: "17px",
          }}
        >
          RADON REPORT
        </Typography>
      </div>

      <Grid
        style={{
          paddingLeft: "18px",
          paddingRight: "18px",
          flexDirection: "row",
        }}
        container
      >
        <Grid
          item
          xs={5}
          style={{
            backgroundColor: "white",
          }}
        >
          <Typography style={styles.reportItemTitle}>
            Device Location
          </Typography>
          <Box style={styles.reportItemContent}>{deviceLocationReturn()}</Box>
        </Grid>

        <Grid
          item
          xs={4}
          style={{
            backgroundColor: "white",
          }}
        >
          <Typography style={styles.reportItemTitle}>Client Info</Typography>
          <Box style={styles.reportItemContent}>{clientReturn()}</Box>
        </Grid>

        <Grid
          item
          xs={3}
          style={{
            backgroundColor: "white",
          }}
        >
          <Typography style={styles.reportItemTitle}>Test By</Typography>
          <Box style={styles.reportItemContent}>{testByReturn()}</Box>
        </Grid>
      </Grid>

      <div
        style={{
          border: "1px solid #E6E8F0",
          borderRadius: 10,
          marginTop: 10,
          marginBottom: 10,
          marginLeft: 20,
          marginRight: 20,
        }}
      >
        <Typography
          style={{
            marginTop: 10,
            color: "black",
            textAlign: "center",
            fontSize: "12px",
          }}
        >
          Test Result:
          {convertToPicocuries(reportDetails.avg_radon) >= 4.0 ? (
            <span
              style={{
                fontSize: "15px",
                fontWeight: 500,
                color: REPORT_RESULT_COLOR[0],
              }}
            >
            <span display='flex' flexDirection='row'>
              <svg fill="#de7548" width="15px" height="15px" viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path d="M 6 5 L 6 7.15625 L 22.53125 14 L 6 20.84375 L 6 23 L 26 14.78125 L 26 13.21875 Z M 6 25 L 6 27 L 26 27 L 26 25 Z"></path></g></svg>&nbsp;{REPORT_RESULT_SU[0]}
            </span>
            </span>
          ) : (
            <span
              style={{
                fontSize: "15px",
                fontWeight: 500,
                color: REPORT_RESULT_COLOR[1],
              }}
            >
          <span
            style={{
              marginLeft: 5,
              fontSize: "15px",
              fontWeight: 500,
              color: REPORT_RESULT_COLOR[0],
            }}
          >
            <span display='flex' flexDirection='row'>
              <svg fill="#de7548" width="15px" height="15px" viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path d="M 26 6 L 6 15.21875 L 6 16.78125 L 26 26 L 26 23.84375 L 9.46875 16 L 26 8.15625 Z"></path></g></svg>&nbsp;{REPORT_RESULT_SU[0]}
            </span>
            </span>
            </span>
          )}
        </Typography>

        <Typography
          style={{
            marginBottom: 10, //20230713
            color: "black",
            textAlign: "center",
            fontSize: "12px",
          }}
        >
          Overall Average:
          <span style={{ marginLeft: 5, fontSize: "17px", fontWeight: 500 }}>
            {reformatRadonData(
              reportDetails.avg_radon,
              reportDetails.measurement_unit,
              1
            )}
          </span>
        </Typography>
      </div>

      <Grid
        style={{
          paddingLeft: 18,
          paddingRight: 18,
          flexDirection: "row",
        }}
        container
      >
        <Grid
          item
          xs={5}
          style={{
            backgroundColor: "white",
          }}
        >
          <Typography style={styles.reportItemTitle}>Test Summary:</Typography>

          <Typography style={styles.titleTopText}>
            Device Name:
            <span style={styles.contentText}>
              {reportDetails.device_name ? reportDetails.device_name : "N/A"}
            </span>
          </Typography>

          <Typography style={styles.titleText}>
            Serial Number:
            <span style={styles.contentText}>
              {reportDetails.serial_number
                ? reportDetails.serial_number
                : "N/A"}
            </span>
          </Typography>

          <Typography style={styles.titleText}>Device Activated</Typography>
          <Typography style={styles.titleText}>
            From:
            <span style={styles.contentText}>
              {reportDetails.activated ? reportDetails.activated : "N/A"}
            </span>
          </Typography>
          <Typography style={styles.titleText}>
            To:
            <span style={styles.contentText}>
              {reportDetails.deactivated ? reportDetails.deactivated : "N/A"}
            </span>
          </Typography>
        </Grid>

        <Grid
          item
          xs={4}
          style={{
            backgroundColor: "white",
          }}
        >
          <Typography style={styles.reportItemTitle}></Typography>

          <Typography
            style={{
              marginTop: 20,
              color: "black",
              fontSize: "11px",
              fontWeight: 300,
              textAlign: "left",
            }}
          >
            Test Floor:
            <span style={styles.contentText}>
              {TEST_FLOOR_KEY[reportDetails.device_placement.location_floor]
                ? TEST_FLOOR_KEY[reportDetails.device_placement.location_floor]
                : "N/A"}
            </span>
          </Typography>

          <Typography style={styles.titleText}>
            Mitigation system:
            <span style={styles.contentText}>
              {MITIGATION_SYSTEM_KEY[
                reportDetails.device_placement.mitigation_installed
              ]
                ? MITIGATION_SYSTEM_KEY[
                    reportDetails.device_placement.mitigation_installed
                  ]
                : "N/A"}
            </span>
          </Typography>

          <Typography style={styles.titleText}>
            Building Type:
            <span style={styles.contentText}>
              {BUILDING_TYPE_KEY[reportDetails.device_placement.bld_type]
                ? BUILDING_TYPE_KEY[reportDetails.device_placement.bld_type]
                : "N/A"}
            </span>
          </Typography>

          <Typography style={styles.titleText}>
            Room Type:
            <span style={styles.contentText}>
              {ROOM_TYPE_KEY[reportDetails.device_placement.room_type]
                ? ROOM_TYPE_KEY[reportDetails.device_placement.room_type]
                : "N/A"}
            </span>
          </Typography>

          <Typography style={styles.titleText}>
            Structure Type:
            <span style={styles.contentText}>
              {STRUCTURE_TYPE_KEY[reportDetails.device_placement.str_type]
                ? STRUCTURE_TYPE_KEY[reportDetails.device_placement.str_type]
                : "N/A"}
            </span>
          </Typography>

          <Typography style={styles.titleText}>
            Year Built:
            <span style={styles.contentText}>
              {reportDetails.device_placement.year_built
                ? reportDetails.device_placement.year_built
                : "N/A"}
            </span>
          </Typography>
        </Grid>

        <Grid item xs={3} style={{ flex: 1, backgroundColor: "white" }}>
          <Typography style={styles.reportItemTitle}>
            Radon Concentration (
            {MEASUREMENT_UNIT[reportDetails.measurement_unit]
              ? MEASUREMENT_UNIT[reportDetails.measurement_unit]
              : "N/A"}
            )
          </Typography>

          <Typography style={styles.titleTopText}>
            Min:
            <span style={styles.contentText}>
              {getRadonValue(
                reportDetails.measurement_unit,
                reportDetails.lowest_radon
              )}
            </span>
          </Typography>

          <Typography style={styles.titleText}>
            Average:
            <span style={styles.contentText}>
              {getRadonValue(
                reportDetails.measurement_unit,
                reportDetails.avg_radon
              )}
            </span>
          </Typography>

          <Typography style={styles.titleText}>
            Max:
            <span style={styles.contentText}>
              {getRadonValue(
                reportDetails.measurement_unit,
                reportDetails.highest_radon
              )}
            </span>
          </Typography>

          <Typography style={styles.titleText}>
            Duration:
            <span style={styles.contentText}>
              {reportDetails.measurement_duration
                ? reportDetails.measurement_duration
                : "N/A"}
            </span>
          </Typography>
        </Grid>
      </Grid>

      <Typography
        style={{
          marginTop: 10,
          marginLeft: 20,
          color: "black",
          fontSize: "10px",
          fontWeight: 500,
          textAlign: "left",
          fontStyle: "italic",
        }}
      >
        Comments:
      </Typography>
      <Typography
        style={{
          marginLeft: 20,
          marginRight: 20,
          marginTop: 5,
          marginBottom: 15,
          color: "black",
          textAlign: "left",
          fontSize: "11px",
          fontWeight: 300,
        }}
      >
        {reportDetails.device_placement.comments}
      </Typography>

      <Typography
        style={{
          marginTop: 30,
          marginLeft: 20,
          color: "black",
          fontSize: "10px",
          fontWeight: 500,
          textAlign: "left",
          fontStyle: "italic",
        }}
      >
        Radon Historical Trend
      </Typography>

      <Grid
        container
        style={{
          paddingLeft: "15px",
          paddingRight: "15px",
          paddingTop: "10px",
        }}
      >
        <Grid item xs={12}>
          <ResponsiveContainer width="100%" aspect={3}>
            <LineChart
              style={{ backgroundColor: "white" }}
              data={reportChartRadonValue}
              margin={{
                right: 10,
                left: -30,
              }}
            >
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="data_no" style={{ fontSize: "10px" }} />
              <YAxis style={{ fontSize: "10px" }} />
              {reportDetails.avg_radon && (
                <Tooltip
                  content={<CustomTooltip />}
                  wrapperStyle={{
                    backgroundColor: "white",
                    outline: "none",
                    boxShadow: "2px 3px 10px rgba(53, 64, 82, 0.2)",
                  }}
                />
              )}
              <Line
                type="monotone"
                dataKey="radon_level"
                stroke="#3DB5E6"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Grid>
      </Grid>
      <Divider
        style={{ marginTop: "5px", marginLeft: "18px", marginRight: "18px" }}
      />
      <Grid
        container
        style={{
          height: "100%",
          paddingTop: "5px",
          paddingLeft: "18px",
          paddingRight: "18px",
        }}
      >
        <Grid item xs={6}>
          <Typography variant={"body2"} style={styles.report_footer}>
            EcoGuard Radon Report
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography
            variant={"body2"}
            style={styles.report_footer}
            align={"right"}
          >
            Report Generated on {generated}
          </Typography>
        </Grid>
      </Grid>
    </div>
  );
};

export default PageMain;
